<template>
  <div>
    <!-- Scanner Button -->
    <el-button
      v-if="!isScanning"
      @click="startScanning"
      :loading="loading"
      :disabled="disabled"
      :type="buttonType"
      :size="buttonSize"
      :icon="icon"
      :circle="circle"
      :plain="plain"
      class="barcode-scanner-button"
    >
      <template v-if="$slots.default">
        <slot></slot>
      </template>
      <template v-else-if="!circle">
        <el-icon><VideoCamera /></el-icon>
        {{ buttonText || t('barcodeScanner.scanBarcode') }}
      </template>
      <template v-else>
        <el-icon><VideoCamera /></el-icon>
      </template>
    </el-button>

    <!-- Scanner Modal -->
    <el-dialog
      v-model="isScanning"
      :title="modalTitle || t('barcodeScanner.scanningBarcode')"
      width="600px"
      :before-close="stopScanning"
      class="barcode-scanner-modal"
      center
      :z-index="10001"
      :close-on-click-modal="false"
      :close-on-press-escape="true"
      :show-close="true"
      :modal="true"
    >
      <div class="scanner-container">
        <!-- Video Element -->
        <div class="video-container">
          <video
            ref="videoElement"
            class="scanner-video"
            autoplay
            muted
            playsinline
          ></video>
          
          <!-- Overlay with scanning frame -->
          <div class="scanner-overlay">
            <div class="scanner-frame">
              <div class="corner top-left"></div>
              <div class="corner top-right"></div>
              <div class="corner bottom-left"></div>
              <div class="corner bottom-right"></div>
            </div>
          </div>
          
          <!-- Loading indicator -->
          <div v-if="scannerLoading" class="scanner-loading">
            <el-icon class="is-loading scanner-loading-icon"><Loading /></el-icon>
            <p>{{ t('barcodeScanner.initializing') }}</p>
          </div>
        </div>

        <!-- Instructions -->
        <div class="scanner-instructions">
          <p>{{ instructions || t('barcodeScanner.instructions') }}</p>
          <div class="scanner-controls">
            <el-button @click="toggleFlash" v-if="flashAvailable" :type="flashOn ? 'primary' : 'default'">
              <i class="fas fa-lightbulb"></i>
              {{ flashOn ? t('barcodeScanner.flashOff') : t('barcodeScanner.flashOn') }}
            </el-button>
            <el-button @click="stopScanning" type="default">
              <el-icon><Close /></el-icon>
              {{ t('common.cancel') }}
            </el-button>
          </div>
        </div>

      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { ElNotification } from 'element-plus'
import { VideoCamera, Loading, Close } from '@element-plus/icons-vue'
import { BrowserMultiFormatReader, NotFoundException } from '@zxing/library'

export default {
  name: 'BarcodeScanner',
  components: {
    VideoCamera,
    Loading,
    Close
  },
  props: {
    // Button styling props
    buttonText: String,
    buttonType: {
      type: String,
      default: 'primary'
    },
    buttonSize: {
      type: String,
      default: 'default'
    },
    circle: {
      type: Boolean,
      default: false
    },
    plain: {
      type: Boolean,
      default: false
    },
    icon: String,
    disabled: {
      type: Boolean,
      default: false
    },
    
    // Scanner props
    modalTitle: String,
    instructions: String,
    autoClose: {
      type: Boolean,
      default: true
    },
    playSound: {
      type: Boolean,
      default: true
    },
    vibrate: {
      type: Boolean,
      default: true
    }
  },
  emits: ['scan-success', 'scan-error', 'scanner-opened', 'scanner-closed'],
  setup(props, { emit }) {
    const { t } = useI18n()
    
    const isScanning = ref(false)
    const scannerLoading = ref(false)
    const loading = ref(false)
    const flashOn = ref(false)
    const flashAvailable = ref(false)
    const lastScanResult = ref('')
    const videoElement = ref(null)
    const isProcessingScan = ref(false)
    const lastScanTime = ref(0)
    
    let codeReader = null
    let currentStream = null
    
    const playBeepSound = () => {
      if (!props.playSound) return
      
      try {
        // Use the same audio file as the mobile app
        const audio = new Audio('/sounds/beep.mp3')
        audio.volume = 1.0 // Maximum volume like mobile app
        audio.play().catch(error => {
          console.warn('Could not play beep audio file:', error)
          
          // Fallback to synthetic beep if audio file fails
          try {
            const audioContext = new (window.AudioContext || window.webkitAudioContext)()
            const oscillator = audioContext.createOscillator()
            const gainNode = audioContext.createGain()
            
            oscillator.connect(gainNode)
            gainNode.connect(audioContext.destination)
            
            oscillator.frequency.value = 1000 // 1kHz beep
            gainNode.gain.value = 0.3
            
            oscillator.start()
            oscillator.stop(audioContext.currentTime + 0.2) // 200ms beep
          } catch (fallbackError) {
            console.warn('Fallback beep sound also failed:', fallbackError)
          }
        })
      } catch (error) {
        console.warn('Could not initialize beep sound:', error)
      }
    }
    
    const triggerVibration = () => {
      if (!props.vibrate) return
      
      try {
        if (navigator.vibrate) {
          navigator.vibrate(200) // 200ms vibration
        }
      } catch (error) {
        console.warn('Could not trigger vibration:', error)
      }
    }
    
    const startScanning = async () => {
      loading.value = true
      
      try {
        isScanning.value = true
        scannerLoading.value = true
        emit('scanner-opened')
        
        // Wait for the modal to render
        await new Promise(resolve => setTimeout(resolve, 100))
        
        // Initialize the code reader
        codeReader = new BrowserMultiFormatReader()
        
        // Check for available video devices
        const videoInputDevices = await codeReader.listVideoInputDevices()
        
        if (videoInputDevices.length === 0) {
          throw new Error(t('barcodeScanner.noCamera'))
        }
        
        // Try to get the back camera (rear camera) if available
        let selectedDeviceId = videoInputDevices[0].deviceId
        const backCamera = videoInputDevices.find(device => 
          device.label.toLowerCase().includes('back') || 
          device.label.toLowerCase().includes('rear') ||
          device.label.toLowerCase().includes('environment')
        )
        if (backCamera) {
          selectedDeviceId = backCamera.deviceId
        }
        
        scannerLoading.value = false
        
        // Set up continuous scanning
        codeReader.decodeFromVideoDevice(selectedDeviceId, videoElement.value, (result, err) => {
          if (result) {
            handleScanSuccess(result.getText())
          }
          if (err && !(err instanceof NotFoundException)) {
            console.warn('Scan error:', err)
          }
        })
        
        // Get the stream to check for flash capability after video is started
        setTimeout(() => {
          currentStream = videoElement.value.srcObject
          if (currentStream) {
            const videoTrack = currentStream.getVideoTracks()[0]
            if (videoTrack && videoTrack.getCapabilities) {
              const capabilities = videoTrack.getCapabilities()
              flashAvailable.value = !!capabilities.torch
            }
          }
        }, 500)
        
      } catch (error) {
        console.error('Scanner error:', error)
        scannerLoading.value = false
        loading.value = false
        isScanning.value = false
        
        ElNotification({
          title: t('common.error'),
          message: error.message || t('barcodeScanner.scannerError'),
          type: 'error',
          position: 'bottom-right'
        })
        
        emit('scan-error', error)
      }
      
      loading.value = false
    }
    
    const handleScanSuccess = (scannedText) => {
      const currentTime = Date.now()
      
      // Prevent duplicate scans within 2 seconds
      if (isProcessingScan.value || (currentTime - lastScanTime.value < 2000 && lastScanResult.value === scannedText)) {
        return
      }
      
      // Lock processing to prevent multiple concurrent scans
      isProcessingScan.value = true
      lastScanTime.value = currentTime
      lastScanResult.value = scannedText
      
      // Provide feedback
      playBeepSound()
      triggerVibration()
      
      // Emit success event
      emit('scan-success', scannedText)
      
      // Note: Success notification is handled by parent component
      
      // Auto close if enabled
      if (props.autoClose) {
        setTimeout(() => {
          stopScanning()
        }, 1000)
      }
      
      // Unlock processing after a delay
      setTimeout(() => {
        isProcessingScan.value = false
      }, 1500)
    }
    
    const stopScanning = () => {
      if (codeReader) {
        codeReader.reset()
        codeReader = null
      }
      
      if (currentStream) {
        currentStream.getTracks().forEach(track => track.stop())
        currentStream = null
      }
      
      isScanning.value = false
      scannerLoading.value = false
      flashOn.value = false
      flashAvailable.value = false
      isProcessingScan.value = false // Reset processing state
      emit('scanner-closed')
    }
    
    const toggleFlash = async () => {
      if (!currentStream || !flashAvailable.value) return
      
      try {
        const videoTrack = currentStream.getVideoTracks()[0]
        if (videoTrack && videoTrack.applyConstraints) {
          await videoTrack.applyConstraints({
            advanced: [{ torch: !flashOn.value }]
          })
          flashOn.value = !flashOn.value
        }
      } catch (error) {
        console.warn('Could not toggle flash:', error)
        ElNotification({
          title: t('common.error'),
          message: t('barcodeScanner.flashError'),
          type: 'warning',
          position: 'bottom-right'
        })
      }
    }
    
    onUnmounted(() => {
      stopScanning()
    })
    
    return {
      t,
      isScanning,
      scannerLoading,
      loading,
      flashOn,
      flashAvailable,
      lastScanResult,
      videoElement,
      isProcessingScan,
      startScanning,
      stopScanning,
      toggleFlash
    }
  }
}
</script>

<style scoped>
.barcode-scanner-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.barcode-scanner-modal {
  max-width: 800px;
}

.scanner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  background: #ffffff;
  border-radius: 8px;
  padding: 8px;
}

.video-container {
  position: relative;
  width: 100%;
  max-width: 600px;
  border-radius: 8px;
  overflow: hidden;
  background: #000;
  border: 1px solid #e4e7ed;
}

.scanner-video {
  width: 100%;
  height: 450px;
  object-fit: cover;
  display: block;
}

.scanner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.scanner-frame {
  position: relative;
  width: 250px;
  height: 150px;
  border: 2px solid #409eff;
  border-radius: 8px;
  background: rgba(64, 158, 255, 0.05);
}

.corner {
  position: absolute;
  width: 20px;
  height: 20px;
  border: 3px solid #409eff;
  border-radius: 2px;
}

.corner.top-left {
  top: -3px;
  left: -3px;
  border-right: none;
  border-bottom: none;
}

.corner.top-right {
  top: -3px;
  right: -3px;
  border-left: none;
  border-bottom: none;
}

.corner.bottom-left {
  bottom: -3px;
  left: -3px;
  border-right: none;
  border-top: none;
}

.corner.bottom-right {
  bottom: -3px;
  right: -3px;
  border-left: none;
  border-top: none;
}

.scanner-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  color: white;
  background: rgba(0, 0, 0, 0.7);
  padding: 16px;
  border-radius: 8px;
}

.scanner-loading-icon {
  font-size: 24px;
  margin-bottom: 10px;
}

.scanner-instructions {
  text-align: center;
  color: #606266;
  font-size: 14px;
  margin-bottom: 16px;
}

.scanner-controls {
  margin-top: 16px;
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
}

.last-scan-result {
  text-align: center;
  padding: 12px;
  background: #f0f9ff;
  border: 1px solid #b3d8ff;
  border-radius: 6px;
  color: #1f2937;
  max-width: 100%;
  word-break: break-all;
}

/* Modal z-index fixes */
:deep(.barcode-scanner-modal) {
  z-index: 10001 !important;
}

:deep(.barcode-scanner-modal .el-dialog__wrapper) {
  z-index: 10001 !important;
}

:deep(.barcode-scanner-modal .el-overlay) {
  z-index: 10000 !important;
  background-color: rgba(0, 0, 0, 0.6) !important;
}

/* Minimal modal styling */
:deep(.barcode-scanner-modal .el-dialog) {
  border-radius: 12px !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15) !important;
  background: #ffffff !important;
}

:deep(.barcode-scanner-modal .el-dialog__header) {
  background: #ffffff !important;
  border-radius: 12px 12px 0 0 !important;
  padding: 16px 20px !important;
  border-bottom: 1px solid #e4e7ed !important;
}

:deep(.barcode-scanner-modal .el-dialog__title) {
  color: #303133 !important;
  font-weight: 500 !important;
  font-size: 16px !important;
}

:deep(.barcode-scanner-modal .el-dialog__headerbtn) {
  top: 16px !important;
  right: 20px !important;
  width: 28px !important;
  height: 28px !important;
  border-radius: 6px !important;
  background: #f5f7fa !important;
  border: 1px solid #dcdfe6 !important;
}

:deep(.barcode-scanner-modal .el-dialog__headerbtn:hover) {
  background: #ecf5ff !important;
  border-color: #409eff !important;
}

:deep(.barcode-scanner-modal .el-dialog__close) {
  color: #909399 !important;
  font-size: 14px !important;
}

:deep(.barcode-scanner-modal .el-dialog__body) {
  padding: 12px !important;
  background: #ffffff !important;
}

/* Responsive design */
@media (max-width: 768px) {
  .scanner-video {
    height: 300px;
  }
  
  .scanner-frame {
    width: 200px;
    height: 120px;
  }
  
  .scanner-controls {
    flex-direction: column;
    align-items: center;
  }
  
  .scanner-container {
    padding: 12px;
    gap: 16px;
  }
}

@media (max-width: 480px) {
  :deep(.barcode-scanner-modal .el-dialog) {
    width: 95% !important;
    margin: 0 auto;
  }
  
  .scanner-frame {
    width: 180px;
    height: 100px;
  }
  
  .scanner-container {
    padding: 8px;
    gap: 12px;
  }
}
</style>
